import React, { useState, useEffect, useCallback } from 'react';
import { WidgetConfig, ContactFormData, DpeInfo } from '../types';
import { verifyListDPE, callHector } from '../api/dpe';
import ContactModal from './ContactModal';

interface Address {
  label: string;
  housenumber: string;
  street: string;
  postcode: string;
  city: string;
}

interface WidgetButtonProps {
  config: WidgetConfig;
  sessionData?: any;
}

const WidgetButton: React.FC<WidgetButtonProps> = ({ config, sessionData }) => {
  const { position, buttonColor = "#1a1a1a" } = config;
  
  // États pour la gestion automatique du widget
  const [isModalOpen, setIsModalOpen] = useState(false);
  
  // États pour la gestion du formulaire (comme dans ContactModal)
  const [formData, setFormData] = useState<ContactFormData>({
    nom: '',
    prenom: '',
    mail: '',
    adresse: '',
    numeroDpe: '',
    telephone: '',
    paysCode: '+33',
    professionnelsRge: false,
    conditionsGenerales: false
  });

  // États pour la validation
  const [errors, setErrors] = useState<Partial<Record<keyof ContactFormData, string>>>({});
  const [touched, setTouched] = useState<Partial<Record<keyof ContactFormData, boolean>>>({});

  // États pour l'aide à la saisie d'adresse
  const [suggestions, setSuggestions] = useState<Address[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [isAddressSelected, setIsAddressSelected] = useState(false);
  
  // États pour les éléments détaillés de l'adresse
  const [numeroVoie, setNumeroVoie] = useState('');
  const [nomRue, setNomRue] = useState('');
  const [nomCommune, setNomCommune] = useState('');
  const [codePostal, setCodePostal] = useState('');
  
  // États pour la validation d'adresse
  const [isValidatingAddress, setIsValidatingAddress] = useState(false);
  const [addressValidationError, setAddressValidationError] = useState('');
  
  // États pour les DPE
  const [dpeList, setDpeList] = useState<DpeInfo[]>([]);
  const [showDpeSuggestions, setShowDpeSuggestions] = useState(false);
  const [isLoadingDpe, setIsLoadingDpe] = useState(false);

  // États pour l'appel API et la modal de confirmation
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);

  // États pour le popup DPE
  const [showDpePopup, setShowDpePopup] = useState(false);
  const [pendingSubmission, setPendingSubmission] = useState(false);
  const [isDpeSelectionInProgress, setIsDpeSelectionInProgress] = useState(false);

  // États pour la pagination DPE
  const [currentDpePage, setCurrentDpePage] = useState(1);
  const [dpeItemsPerPage] = useState(10);

  // Fonctions de validation (copiées de ContactModal)
  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validateTelephone = (telephone: string): boolean => {
    const cleanTelephone = telephone.replace(/[^0-9]/g, '');
    const mobileRegex = /^0[67]\d{8}$/;
    const fixeRegex = /^0[1-59]\d{8}$/;
    return mobileRegex.test(cleanTelephone) || fixeRegex.test(cleanTelephone);
  };

  const validateField = (fieldName: keyof ContactFormData, value: any) => {
    let error = '';

    switch (fieldName) {
      case 'prenom':
        if (!value || !value.trim()) {
          error = 'Le prénom est requis';
        }
        break;
      case 'nom':
        if (!value || !value.trim()) {
          error = 'Le nom est requis';
        }
        break;
      case 'mail':
        if (value && !validateEmail(value)) {
          error = 'Format d\'email invalide';
        }
        break;
      case 'telephone':
        if (value && !validateTelephone(value)) {
          error = 'Format de numéro de téléphone invalide (ex: 0123456789, 01 23 45 67 89)';
        }
        break;
      case 'professionnelsRge':
        if (!value) {
          error = 'Vous devez accepter d\'être mis en relation avec des professionnels RGE';
        }
        break;
      case 'conditionsGenerales':
        if (!value) {
          error = 'Vous devez accepter les conditions générales';
        }
        break;
    }

    setErrors(prev => ({
      ...prev,
      [fieldName]: error
    }));
  };

  const validateForm = (): boolean => {
    const newErrors: Partial<Record<keyof ContactFormData, string>> = {};
    
    if (!formData.nom.trim()) newErrors.nom = 'Le nom est requis';
    if (!formData.prenom.trim()) newErrors.prenom = 'Le prénom est requis';
    
    if (!formData.mail.trim()) {
      newErrors.mail = 'L\'email est requis';
    } else if (!validateEmail(formData.mail)) {
      newErrors.mail = 'Format d\'email invalide';
    }
    
    if (!formData.adresse.trim()) newErrors.adresse = 'L\'adresse est requise';
    
    if (!formData.telephone.trim()) {
      newErrors.telephone = 'Le téléphone est requis';
    } else if (!validateTelephone(formData.telephone)) {
      newErrors.telephone = 'Format de numéro de téléphone invalide (ex: 0123456789, 01 23 45 67 89)';
    }
    
    if (!formData.professionnelsRge) {
      newErrors.professionnelsRge = 'Vous devez accepter d\'être mis en relation avec des professionnels RGE';
    }
    
    if (!formData.conditionsGenerales) {
      newErrors.conditionsGenerales = 'Vous devez accepter les conditions générales';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Fonction pour valider l'adresse avec l'API
  const validateAddress = async (address: string): Promise<boolean> => {
    if (!address.trim()) {
      setAddressValidationError('L\'adresse est requise');
      return false;
    }

    setIsValidatingAddress(true);
    setAddressValidationError('');

    try {
      const addressValidationResponse = await fetch(
        `https://api-adresse.data.gouv.fr/search/?q=${encodeURIComponent(address)}&limit=1`
      );
      const addressValidationData = await addressValidationResponse.json();

      if (!addressValidationData.features || addressValidationData.features.length === 0) {
        setAddressValidationError('L\'adresse que vous avez saisie n\'a pas été trouvée. Veuillez saisir de nouveau une adresse valide');
        setIsValidatingAddress(false);
        return false;
      }

      setIsValidatingAddress(false);
      return true;
    } catch (addressError) {
      console.error('Erreur lors de la validation de l\'adresse:', addressError);
      setAddressValidationError('Erreur lors de la validation de l\'adresse. Veuillez réessayer.');
      setIsValidatingAddress(false);
      return false;
    }
  };

  // Fonction pour récupérer la liste des DPE
  const fetchDpeList = async (adresse: string, numeroVoie: string, nomRue: string, nomCommune: string, codePostal: string) => {
    console.log('fetchDpeList appelée avec:', { adresse, numeroVoie, nomRue, nomCommune, codePostal });
    
    if (!adresse || !nomRue || !nomCommune || !codePostal) {
      console.log('Paramètres manquants, arrêt de fetchDpeList');
      return;
    }

    console.log('Début de la récupération des DPE...');
    setIsLoadingDpe(true);
    try {
      console.log('Appel API verifyListDPE...');
      const dpeList = await verifyListDPE(adresse, numeroVoie, nomRue, nomCommune, codePostal);
      console.log('Réponse API DPE:', dpeList);
      setDpeList(dpeList);
      resetDpePagination();
      console.log('DPE récupérés et mis à jour:', dpeList);
    } catch (error) {
      console.error('Erreur lors de la récupération des DPE:', error);
      setDpeList([]);
    } finally {
      setIsLoadingDpe(false);
      console.log('Chargement DPE terminé');
    }
  };

  const resetDpePagination = useCallback(() => {
    setCurrentDpePage(1);
  }, [setCurrentDpePage]);

  // Fonction pour nettoyer toutes les données
  const clearAllData = useCallback(() => {
    console.log('🧹 Nettoyage de toutes les données...');
    
    setFormData({
      nom: '',
      prenom: '',
      mail: '',
      adresse: '',
      numeroDpe: '',
      telephone: '',
      paysCode: '+33',
      professionnelsRge: false,
      conditionsGenerales: false
    });
    
    setNumeroVoie('');
    setNomRue('');
    setNomCommune('');
    setCodePostal('');
    setSuggestions([]);
    setShowSuggestions(false);
    setIsAddressSelected(false);
    setAddressValidationError('');
    setIsValidatingAddress(false);
    
    setTouched({});
    setErrors({});
    
    setDpeList([]);
    setShowDpeSuggestions(false);
    setIsLoadingDpe(false);
    setShowDpePopup(false);
    setPendingSubmission(false);
    setIsDpeSelectionInProgress(false);
    resetDpePagination();
    
    setIsSubmitting(false);
    setShowConfirmationModal(false);
    
    sessionStorage.removeItem('hectorSession');
    
    console.log('✅ Toutes les données ont été nettoyées');
  }, [setFormData, setNumeroVoie, setNomRue, setNomCommune, setCodePostal, setSuggestions, setShowSuggestions, setIsAddressSelected, setAddressValidationError, setIsValidatingAddress, setTouched, setErrors, setDpeList, setShowDpeSuggestions, setIsLoadingDpe, setShowDpePopup, setPendingSubmission, setIsDpeSelectionInProgress, resetDpePagination, setIsSubmitting, setShowConfirmationModal]);

  // Fonction pour traiter la soumission finale
  const processSubmission = useCallback(async (numeroDpeOverride?: string) => {
    setIsSubmitting(true);
    
    try {
      // Formater le téléphone au format international
      const cleanTelephone = formData.telephone.replace(/[\s.-]/g, '');
      
      let formattedTelephone = cleanTelephone;
      if (cleanTelephone.startsWith('0')) {
        const countryCode = formData.paysCode.substring(1); 
        formattedTelephone = countryCode + cleanTelephone.substring(1);
      } else{
        const countryCode = formData.paysCode.substring(1); 
        formattedTelephone = countryCode + cleanTelephone;
      }
      
      formattedTelephone = '+' + formattedTelephone;
      
      console.log('Téléphone formaté:', formattedTelephone);
      
      const numeroDpeToUse = numeroDpeOverride !== undefined ? numeroDpeOverride : formData.numeroDpe;
      console.log('Numéro DPE utilisé pour la soumission:', numeroDpeToUse);
      
      // Appel API callHector
      const result = await callHector(
        formData.nom,
        formData.prenom,
        formData.mail,
        formData.adresse,
        numeroVoie,
        nomRue,
        nomCommune,
        codePostal,
        numeroDpeToUse,
        formattedTelephone,
        formData.professionnelsRge ? 'Oui' : 'Non',
        formData.conditionsGenerales? 'Oui' : 'No',
        sessionData?.sessionId || ''
      );
      
      if (result.success) {
        // Effacer toutes les données du formulaire
        clearAllData();
        
        // Ouvrir la modal de confirmation
        setShowConfirmationModal(true);
      } else {
        alert(`Erreur: ${result.message}`);
      }
    } catch (error) {
      console.error('Erreur lors de l\'appel API:', error);
      alert('Une erreur est survenue lors de l\'envoi de votre demande.');
    } finally {
      setIsSubmitting(false);
      setPendingSubmission(false);
    }
  }, [formData, numeroVoie, nomRue, nomCommune, codePostal, sessionData, clearAllData]);

  // Fonctions de pagination DPE
  const getCurrentPageDpes = () => {
    const startIndex = (currentDpePage - 1) * dpeItemsPerPage;
    const endIndex = startIndex + dpeItemsPerPage;
    return dpeList.slice(startIndex, endIndex);
  };

  const getTotalPages = () => {
    return Math.ceil(dpeList.length / dpeItemsPerPage);
  };

  const handleDpePageChange = (page: number) => {
    setCurrentDpePage(page);
  };

  // Fonction pour obtenir la couleur selon la classe DPE
  const getDpeClassColor = (classe: string) => {
    const classeUpper = classe.toUpperCase();
    switch (classeUpper) {
      case 'A':
        return { bg: 'bg-green-800', text: 'text-white', border: 'border-green-800' };
      case 'B':
        return { bg: 'bg-green-600', text: 'text-white', border: 'border-green-600' };
      case 'C':
        return { bg: 'bg-green-400', text: 'text-white', border: 'border-green-400' };
      case 'D':
        return { bg: 'bg-yellow-400', text: 'text-gray-900', border: 'border-yellow-400' };
      case 'E':
        return { bg: 'bg-orange-500', text: 'text-white', border: 'border-orange-500' };
      case 'F':
        return { bg: 'bg-orange-600', text: 'text-white', border: 'border-orange-600' };
      case 'G':
        return { bg: 'bg-red-600', text: 'text-white', border: 'border-red-600' };
      default:
        return { bg: 'bg-gray-400', text: 'text-white', border: 'border-gray-400' };
    }
  };
  
  // Fonction pour ouvrir la modal
  const handleOpenModal = () => {
    console.log('🖱️ Clic sur le bouton du widget détecté');
    console.log('📱 Ouverture de la modal...');
    console.log('🔍 État actuel isModalOpen:', isModalOpen);
    setIsModalOpen(true);
    console.log('✅ Modal ouverte, isModalOpen:', true);
  };
  
  // Fonction pour fermer la modal
  const handleCloseModal = () => {
    clearAllData();
    setIsModalOpen(false);
  };
  
  // Fonction pour gérer la soumission du formulaire
  const handleFormSubmit = async (data: ContactFormData) => {
    console.log('📝 Données du formulaire reçues:', data);
    
    // Mettre à jour le formData local
    setFormData(data);
    
    // Marquer tous les champs comme touchés pour afficher les erreurs
    const allFields: (keyof ContactFormData)[] = ['nom', 'prenom', 'mail', 'adresse', 'numeroDpe', 'telephone', 'professionnelsRge', 'conditionsGenerales'];
    const touchedFields = allFields.reduce((acc, field) => ({ ...acc, [field]: true }), {});
    setTouched(prev => ({ ...prev, ...touchedFields }));
    
    // Valider le formulaire de base
    if (!validateForm()) {
      return;
    }

    // Valider l'adresse avec l'API
    const isAddressValid = await validateAddress(data.adresse);
    if (!isAddressValid) {
      return;
    }
    
    // Marquer qu'une soumission est en attente
    setPendingSubmission(true);
    
    // L'useEffect gérera l'ouverture du popup ou la soumission directe
    // selon l'état des DPE (chargés, en cours, ou non trouvés)
  };

  // Fonction pour gérer la sélection d'une adresse
  const handleAddressSelect = async (address: Address) => {
    console.log('Adresse sélectionnée:', address);
    
    setFormData(prev => ({
      ...prev,
      adresse: address.label
    }));
    
    // Remplir les éléments détaillés de l'adresse
    setNumeroVoie(address.housenumber || '');
    setNomRue(address.street || '');
    setNomCommune(address.city || '');
    setCodePostal(address.postcode || '');
    
    setShowSuggestions(false);
    setSuggestions([]);
    setIsAddressSelected(true);
    setTouched(prev => ({
      ...prev,
      adresse: true
    }));
    
    // Valider le champ adresse après sélection
    validateField('adresse', address.label);
    
    // Récupérer les DPE pour cette adresse
    const numeroVoie = address.housenumber || '';
    const nomRue = address.street || '';
    const nomCommune = address.city || '';
    const codePostal = address.postcode || '';
    
    // Récupérer les DPE en parallèle lors de la sélection d'adresse
    if (address.label && codePostal) {
      fetchDpeList(address.label, numeroVoie, nomRue, nomCommune, codePostal);
    } else {
      console.log('Adresse ou code postal manquant, impossible de récupérer les DPE');
    }
  };

  // Fonction pour gérer la sélection d'un DPE
  const handleDpeSelect = async (dpe: DpeInfo) => {
    console.log('DPE sélectionné:', dpe);
    console.log('Numéro DPE:', dpe.numeroDpe);
    
    // Marquer qu'une sélection DPE est en cours
    setIsDpeSelectionInProgress(true);
    
    // Fermer le popup DPE immédiatement
    setShowDpePopup(false);
    
    setFormData(prev => {
      const newFormData = {
        ...prev,
        numeroDpe: dpe.numeroDpe
      };
      console.log('Nouveau formData:', newFormData);
      return newFormData;
    });
    setTouched(prev => ({
      ...prev,
      numeroDpe: true
    }));
    
    // Valider le champ DPE après sélection
    validateField('numeroDpe', dpe.numeroDpe);
    
    // Si une soumission est en attente, traiter la soumission
    if (pendingSubmission) {
      console.log('Soumission en attente, traitement avec DPE:', dpe.numeroDpe);
      await processSubmission(dpe.numeroDpe);
    }
  };

  // Fonction pour continuer sans DPE
  const handleContinueWithoutDpe = async () => {
    // Marquer qu'une sélection DPE est en cours
    setIsDpeSelectionInProgress(true);
    
    // Fermer le popup DPE immédiatement
    setShowDpePopup(false);
    
    setFormData(prev => ({
      ...prev,
      numeroDpe: ''
    }));
    
    // Si une soumission est en attente, traiter la soumission
    if (pendingSubmission) {
      console.log('Soumission en attente, traitement sans DPE');
      await processSubmission('');
    }
  };

  // useEffect pour gérer l'ouverture du popup DPE quand une soumission est en attente
  useEffect(() => {
    if (pendingSubmission && !isLoadingDpe && dpeList.length > 0 && !showDpePopup && !isDpeSelectionInProgress) {
      setShowDpePopup(true);
    } else if (pendingSubmission && !isLoadingDpe && dpeList.length === 0 && !isDpeSelectionInProgress) {
      // Aucun DPE trouvé, procéder directement à la soumission
      console.log('Aucun DPE trouvé, soumission directe sans DPE');
      processSubmission('');
    }
  }, [pendingSubmission, isLoadingDpe, dpeList.length, showDpePopup, isDpeSelectionInProgress, processSubmission]);

  const widgetClasses = `
    fixed z-50 w-72 sm:w-80 max-w-sm
    ${position === 'top' ? 'top-2 right-2 sm:top-4 sm:right-4' : 'bottom-2 right-2 sm:bottom-4 sm:right-4'}
    animate-bounce-in
  `;
  
  const widgetStyle: React.CSSProperties = {
    pointerEvents: 'auto' // Permettre les clics sur le widget
  };

  const buttonClasses = `
    w-full font-medium py-3 px-4 sm:py-4 sm:px-6 rounded-xl
    transition-all duration-300 cursor-pointer select-none
    flex items-center space-x-2 sm:space-x-3 shadow-lg hover:shadow-xl
    ${buttonColor === '#f5f5dc' || buttonColor === '#c0c0c0' ? 'text-gray-800' : 'text-white'}
  `;

  return (
    <div className={widgetClasses} style={widgetStyle}>
      {/* Widget Card */}
      <div className="bg-white rounded-2xl shadow-2xl border border-gray-100 overflow-hidden">
        {/* Main Button */}
        <button
          className={buttonClasses}
          style={{ 
            backgroundColor: buttonColor,
            '--hover-color': buttonColor ? `${buttonColor}dd` : '#1a1a1add'
          } as React.CSSProperties}
          onMouseEnter={(e) => {
            // Pour les couleurs claires, on assombrit plus
            const isLightColor = buttonColor === '#f5f5dc' || buttonColor === '#c0c0c0';
            if (isLightColor) {
              e.currentTarget.style.backgroundColor = buttonColor === '#f5f5dc' ? '#e6e6c7' : '#a8a8a8';
            } else {
              e.currentTarget.style.backgroundColor = `${buttonColor}dd`;
            }
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = buttonColor;
          }}
          onClick={handleOpenModal}
          aria-label="Ouvrir le formulaire de contact"
          data-hector-widget-button="true"
        >
          <div className={`flex items-center justify-center w-6 h-6 sm:w-8 sm:h-8 rounded-full ${
            buttonColor === '#f5f5dc' || buttonColor === '#c0c0c0' 
              ? 'bg-gray-800 bg-opacity-20' 
              : 'bg-white bg-opacity-20'
          }`}>
            <svg 
              className="w-4 h-4 sm:w-5 sm:h-5" 
              fill="none" 
              stroke="currentColor" 
              viewBox="0 0 24 24"
            >
              <path 
                strokeLinecap="round" 
                strokeLinejoin="round" 
                strokeWidth={2} 
                d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" 
              />
            </svg>
          </div>
          <span className="text-xs sm:text-sm font-semibold">
            Me faire appeler par un expert
          </span>
        </button>
        
        {/* Preview Section */}
        <div className="p-3 sm:p-4 bg-white border-t border-gray-100">
          <div className="flex items-center justify-between">
            <div className="flex-1 min-w-0">
              <p className="text-sm sm:text-base font-semibold text-gray-900">Call Hector</p>
              <p className="text-xs sm:text-sm text-gray-600 hidden sm:block">Prêt à vous aider en rénovation énergétique</p>
              <p className="text-xs text-gray-600 sm:hidden">Prêt à vous aider</p>
            </div>
            <div className="flex-shrink-0">
              <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Modal de contact intégrée avec toutes les interactions API */}
      <ContactModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        onSubmit={handleFormSubmit}
        buttonColor={buttonColor}
        sessionData={sessionData}
        // Props pour les interactions API
        formData={formData}
        setFormData={setFormData}
        errors={errors}
        setErrors={setErrors}
        touched={touched}
        setTouched={setTouched}
        suggestions={suggestions}
        setSuggestions={setSuggestions}
        showSuggestions={showSuggestions}
        setShowSuggestions={setShowSuggestions}
        isAddressSelected={isAddressSelected}
        setIsAddressSelected={setIsAddressSelected}
        numeroVoie={numeroVoie}
        setNumeroVoie={setNumeroVoie}
        nomRue={nomRue}
        setNomRue={setNomRue}
        nomCommune={nomCommune}
        setNomCommune={setNomCommune}
        codePostal={codePostal}
        setCodePostal={setCodePostal}
        isValidatingAddress={isValidatingAddress}
        setIsValidatingAddress={setIsValidatingAddress}
        addressValidationError={addressValidationError}
        setAddressValidationError={setAddressValidationError}
        dpeList={dpeList}
        setDpeList={setDpeList}
        showDpeSuggestions={showDpeSuggestions}
        setShowDpeSuggestions={setShowDpeSuggestions}
        isLoadingDpe={isLoadingDpe}
        setIsLoadingDpe={setIsLoadingDpe}
        isSubmitting={isSubmitting}
        setIsSubmitting={setIsSubmitting}
        showConfirmationModal={showConfirmationModal}
        setShowConfirmationModal={setShowConfirmationModal}
        showDpePopup={showDpePopup}
        setShowDpePopup={setShowDpePopup}
        pendingSubmission={pendingSubmission}
        setPendingSubmission={setPendingSubmission}
        isDpeSelectionInProgress={isDpeSelectionInProgress}
        setIsDpeSelectionInProgress={setIsDpeSelectionInProgress}
        currentDpePage={currentDpePage}
        setCurrentDpePage={setCurrentDpePage}
        dpeItemsPerPage={dpeItemsPerPage}
        // Fonctions
        validateEmail={validateEmail}
        validateTelephone={validateTelephone}
        validateField={validateField}
        validateForm={validateForm}
        validateAddress={validateAddress}
        fetchDpeList={fetchDpeList}
        processSubmission={processSubmission}
        getCurrentPageDpes={getCurrentPageDpes}
        getTotalPages={getTotalPages}
        handleDpePageChange={handleDpePageChange}
        resetDpePagination={resetDpePagination}
        getDpeClassColor={getDpeClassColor}
        clearAllData={clearAllData}
        handleAddressSelect={handleAddressSelect}
        handleDpeSelect={handleDpeSelect}
        handleContinueWithoutDpe={handleContinueWithoutDpe}
      />
    </div>
  );
};

export default WidgetButton;
